/**
 * useDealRoomRealtime — Supabase Realtime hook for a single Deal Room.
 *
 * Subscribes to INSERT events on `deal_activity` + `messages` for the
 * given `dealId` and calls `onSignal()` whenever something changes. The
 * caller is expected to trigger a reload from FastAPI (which enforces
 * RBAC) in response — we don't trust the payload across the wire.
 *
 * Reconnect behaviour is handled by supabase-js itself. We provide
 * `isConnected` so the UI can subtly indicate live state without
 * splashy badges.
 */
import { useEffect, useRef, useState } from "react";
import { supabase, isRealtimeConfigured } from "../lib/supabaseClient";

export function useDealRoomRealtime(dealId, onSignal) {
  const [isConnected, setIsConnected] = useState(false);
  const onSignalRef = useRef(onSignal);

  useEffect(() => { onSignalRef.current = onSignal; }, [onSignal]);

  useEffect(() => {
    if (!isRealtimeConfigured() || !dealId) return undefined;

    const channel = supabase.channel(`deal-room:${dealId}`, {
      config: { broadcast: { ack: false }, presence: { key: "" } },
    });

    const emit = (table) => (payload) => {
      try {
        if (typeof onSignalRef.current === "function") {
          onSignalRef.current({
            table,
            event: payload?.eventType || "INSERT",
            new: payload?.new || null,
          });
        }
      } catch { /* swallow listener errors */ }
    };

    channel
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "deal_activity", filter: `deal_id=eq.${dealId}` },
        emit("deal_activity")
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `deal_id=eq.${dealId}` },
        emit("messages")
      )
      .subscribe((status) => {
        setIsConnected(status === "SUBSCRIBED");
      });

    return () => {
      setIsConnected(false);
      try { supabase.removeChannel(channel); } catch { /* noop */ }
    };
  }, [dealId]);

  return { isConnected };
}
